/* eslint-disable react/forbid-prop-types */
import React from 'react';
import proptypes from 'prop-types';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { useConfigService } from '@thd-nucleus/experience-context';
import { shimThdCustomerHook, useAddToCart } from '@thd-olt-component-react/cart-common-data';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { PAYPAL_CLIENT_ID, PAYPAL_ENABLE } from '../constants/constants';
import { usePaypalInit } from '../hooks/usePaypalInit';
import { PaypalMessage } from './PaypalMessage';
import { PaypalSmartButton } from './PaypalSmartButton';
import { SavedPaypalButton } from './SavedPaypalButton';

const { string, bool, func, object } = proptypes;

const PaypalCheckoutButton = (props) => {
  const {
    CAACCheckbox = true,
    CAACExperience = false,
    cartRequest,
    checkoutType,
    customer,
    customPaypalHandler,
    disabled,
    ffm,
    fromPage,
    hasSavedPaypal,
    hideButton,
    hideMessage,
    isUpdatePaypal,
    isValidCart,
    itemPrice,
    namespace,
    orderId,
    orderTotal,
    page,
    payInFour,
    paypalButtonStyle,
    triggerGetCart = () => { },
    useSubmitHandler = () => { },
  } = props;

  const [addToCart] = useAddToCart();
  const [initPaypal] = usePaypalInit();
  const [triggerSubmit] = useSubmitHandler({
    checkoutType,
    customer,
    page
  }) || [() => { }];

  const isPaypalEnabled = useConfigService(PAYPAL_ENABLE);
  const paypalClientId = useConfigService(PAYPAL_CLIENT_ID);

  const { isLoggedIn, isPersisted } = shimThdCustomerHook(useThdCustomer());

  if (!isPaypalEnabled) {
    return false;
  }

  const isUserAuth = !isLoggedIn || (isLoggedIn && !isPersisted);

  const showSavedPaypalButton = hasSavedPaypal && !hideButton;
  const showSmartButton = (!hasSavedPaypal || payInFour) && !hideButton;
  const showPayInFourMessage = (!hideMessage && payInFour);
  const hasSavedPaypalAndPayInFour = hasSavedPaypal && payInFour;

  return (
    <div data-component="PaypalCheckoutButton">
      <ErrorBoundary name="paypal-checkout-button">
        <PayPalScriptProvider options={{
          clientId: paypalClientId,
          commit: 'false',
          components: 'buttons,funding-eligibility,messages',
          dataNamespace: namespace,
          disableFunding: 'card',
          enableFunding: 'paylater',
          intent: 'order',
        }}
        >
          <div className="sui-grid sui-grid-cols-2 sui-gap-1">
            {showSavedPaypalButton && (
              <div className={payInFour ? '' : 'sui-col-span-2'}>
                <SavedPaypalButton
                  disabled={disabled}
                  isAuthUser={isUserAuth}
                  isValidCart={isValidCart}
                  payInFour={payInFour}
                />
              </div>
            )}
            {showSmartButton && (
              <div
                className={
                  hasSavedPaypalAndPayInFour
                    ? 'sui-relative sui-z-0'
                    : 'sui-col-span-2 sui-relative sui-z-0'
                }
                data-testid={hasSavedPaypalAndPayInFour ? 'paypal-pay-in-four-button' : 'paypal-smart-button'}
                data-automation-id={hasSavedPaypalAndPayInFour ? 'paypal-pay-in-four-button' : 'paypal-smart-button'}
              >
                <PaypalSmartButton
                  addToCart={addToCart}
                  CAACCheckbox={CAACCheckbox}
                  CAACExperience={CAACExperience}
                  cartRequest={cartRequest}
                  checkoutType={checkoutType}
                  customPaypalHandler={customPaypalHandler}
                  disabled={disabled}
                  ffm={ffm}
                  fromPage={fromPage}
                  hasSavedPaypal={hasSavedPaypal}
                  initPaypal={initPaypal}
                  isUpdatePaypal={isUpdatePaypal}
                  itemPrice={itemPrice}
                  orderId={orderId}
                  payInFour={payInFour}
                  paypalButtonStyle={paypalButtonStyle}
                  triggerGetCart={triggerGetCart}
                  triggerSubmit={triggerSubmit}
                />
              </div>
            )}
            {showPayInFourMessage && (
              <div
                className="sui-col-span-2"
                data-automation-id="paypal-message"
                data-testid="paypal-pay-in-four-message"
              >
                <PaypalMessage
                  fromPage={fromPage}
                  orderTotal={orderTotal}
                />
              </div>
            )}
          </div>
        </PayPalScriptProvider>
      </ErrorBoundary>
    </div>
  );
};

PaypalCheckoutButton.propTypes = {
  CAACCheckbox: bool,
  CAACExperience: bool,
  cartRequest: object,
  checkoutType: string,
  customer: object,
  customPaypalHandler: func,
  disabled: bool,
  ffm: string.isRequired,
  fromPage: string.isRequired,
  hasSavedPaypal: bool,
  hideButton: bool,
  hideMessage: bool,
  isUpdatePaypal: bool,
  isValidCart: bool.isRequired,
  itemPrice: string,
  namespace: string,
  orderId: string,
  orderTotal: string.isRequired,
  page: object,
  payInFour: bool,
  paypalButtonStyle: string,
  triggerGetCart: func,
  useSubmitHandler: func,
};

PaypalCheckoutButton.defaultProps = {
  CAACCheckbox: false,
  CAACExperience: false,
  cartRequest: null,
  checkoutType: '',
  customer: {},
  customPaypalHandler: null,
  disabled: false,
  hasSavedPaypal: false,
  hideButton: false,
  hideMessage: false,
  isUpdatePaypal: false,
  itemPrice: '',
  namespace: 'paypalSdk',
  orderId: '',
  page: {},
  payInFour: false,
  paypalButtonStyle: 'paypal',
  triggerGetCart: () => { },
  useSubmitHandler: () => { },
};

PaypalCheckoutButton.displayName = 'PaypalCheckoutButton';

export { PaypalCheckoutButton };